import { type FC } from 'react';
import { isValid } from 'date-fns';
import { useForm, FormProvider } from 'react-hook-form-latest';
import ApiErrorBanner, { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Loading from '@/components/Loading';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative';
import SearchSelect from '@/components/UI/SearchSelect';
import { _isEmpty, _notNil } from '@/littledash';
import { useRecordBulkObservations, MAX_BULK_OBSERVATION_RECORDS } from './RecordBulkObservationsProvider';
import type { ObservationRowData } from './RecordBulkObservations.utils';
import { ModalHeader } from '@/utils/modal.tsx';
import { RiArrowRightSLine } from 'react-icons/ri';
import ObservationEntryCard from './ObservationEntryCard';
interface ObservationsFormData {
  observations: Array<ObservationRowData>;
}

// Step 1: Select observations and date
const SelectObservationStep: FC = () => {
  const {
    observations,
    observationsLoading,
    selectedObservationIds,
    setSelectedObservationIds,
    observedDate,
    setObservedDate,
    goToStep2,
    handleRecordNormal,
    recordingNormal,
    exceedsRecordNormalLimit,
    exceedsNextLimit,
  } = useRecordBulkObservations();

  const sortObservations = <T extends { title?: string | null }>(observations: Array<T>): Array<T> =>
    [...observations].sort((a, b) => (a?.title ?? '').localeCompare(b?.title ?? ''));

  const dateInvalid = !observedDate || !isValid(new Date(observedDate));
  const hasSelection = !_isEmpty(selectedObservationIds);
  const isDisabled = observationsLoading || dateInvalid || recordingNormal;

  const getNextButtonTooltip = (): string | undefined => {
    if (exceedsNextLimit) {
      return `Exceeds limit of ${MAX_BULK_OBSERVATION_RECORDS.toLocaleString()} observations`;
    }
    if (!hasSelection) {
      return 'Select an observation to continue';
    }
    if (dateInvalid) {
      return 'Select a valid date & time';
    }
    return undefined;
  };

  const getRecordNormalTooltip = (): string | undefined => {
    if (exceedsRecordNormalLimit) {
      return `Exceeds limit of ${MAX_BULK_OBSERVATION_RECORDS.toLocaleString()} observations`;
    }
    if (dateInvalid) {
      return 'Select a valid date & time';
    }
    return undefined;
  };

  if (observationsLoading) {
    return (
      <div className="pv3">
        <Loading txt="Fetching observations" />
      </div>
    );
  }

  return (
    <>
      <div className="pa3">
        <div>
          <div className="pb2">
            <label>Observed at</label>
            <DateTimePickerNative onChange={setObservedDate} value={observedDate} />
          </div>
        </div>
        <div>
          {!_isEmpty(observations) ? (
            <div className="ui-card">
              <SearchSelect
                selected={selectedObservationIds}
                setSelected={setSelectedObservationIds}
                sections={[
                  {
                    title: 'Observations',
                    items: sortObservations(observations ?? []).map((o) => ({
                      id: o.api_id,
                      title: o.title,
                    })),
                  },
                ]}
              />
            </div>
          ) : (
            <Banner info className="mw6" dismiss={false}>
              <h3 className="f5 normal basier-med lh-title">No team glossary items have been added</h3>
              <p className="f6 pt2 lh-copy">
                To get started, click on Glossary in the left hand menu and add observation glossary items.
              </p>
            </Banner>
          )}
        </div>
      </div>
      <div className="ph3 pv3 bt b-moon-gray">
        <Button
          tooltip={getNextButtonTooltip()}
          disabled={!hasSelection || isDisabled || exceedsNextLimit}
          onClick={goToStep2}
        >
          Next
        </Button>
        <Button
          className="ml3"
          outline
          plain
          tooltip={getRecordNormalTooltip()}
          disabled={isDisabled || exceedsRecordNormalLimit}
          onClick={handleRecordNormal}
          loading={recordingNormal}
        >
          Record normal and finish
        </Button>
      </div>
    </>
  );
};

// Step 2: Form-based observation entry
const ReviewObservationsStep: FC = () => {
  const { tableData, submitting, submissionError, handleSubmit, goToStep1 } = useRecordBulkObservations();
  const formMethods = useForm<ObservationsFormData>({
    mode: 'onBlur',
    defaultValues: {
      observations: tableData,
    },
  });

  const {
    handleSubmit: handleFormSubmit,
    formState: { isValid: isFormValid },
  } = formMethods;

  const onSubmit = async (data: ObservationsFormData) => {
    await handleSubmit(data.observations);
  };

  const onFormSubmit = handleFormSubmit(onSubmit);

  return (
    <FormProvider {...formMethods}>
      <div>
        <div
          className="ph3 pv3"
          style={{ maxHeight: '60vh', overflowY: 'auto' }}
          data-test-component="ReviewObservationsStep"
          data-test-element="observations-list"
        >
          {_notNil(submissionError) && (
            <ApiErrorBanner errorType={ApiErrorType.SUBMIT} error={submissionError} className="mb3" />
          )}

          {tableData.map((observation, index) => (
            <ObservationEntryCard
              key={observation.team_glossary_api_id}
              index={index}
              observationData={observation}
              testId={`observation-entry-card-${index}`}
            />
          ))}
        </div>

        <div className="ph3 pv3 bt b-moon-gray flex">
          <div className="pr3">
            <Button onClick={onFormSubmit} disabled={submitting || !isFormValid} loading={submitting}>
              Record observations
            </Button>
          </div>
          <div>
            <Button onClick={goToStep1} plain disabled={submitting}>
              Back
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

// Main form component
export const RecordBulkObservationsForm: FC = () => {
  const { currentStep, animals, closeModal } = useRecordBulkObservations();

  return (
    <>
      <ModalHeader
        title="Record observations"
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText={`Recording observations for ${animals.length} animal(s).`}
      />
      <div>
        <div className="flex flex-row justify-between pa3">
          <div className="flex flex-row items-center justify-start f6 pv2 ph3 ba bg-near-white">
            <div className={currentStep === 1 ? 'fw5 black' : 'fw4'}>Select observation types</div>
            <RiArrowRightSLine className="mh4" />
            <div className={currentStep === 2 ? 'fw5 black' : 'fw4'}>Record observations</div>
          </div>
        </div>
      </div>
      {currentStep === 1 ? <SelectObservationStep /> : <ReviewObservationsStep />}
    </>
  );
};
